import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiConstants from 'shared/api/apiConstants';
import { toast } from 'react-toastify';
import { useRootStore } from '~/app/contexts/RootStoreContext.jsx';
import { STALE_TIME } from 'users/containers/Organization/consts';
import {
  fetchManagementAccounts,
  updateAccountProperties,
  deleteAccount,
  fetchPayerLinkedAccounts,
  updateLinkedAccountData,
  validateLinkedAccounts,
} from './apiManagementAccounts';
import { fetchDataAccessPayerAccounts, fetchLinkedAccounts, fetchBillingAccounts } from './apiDataAccessAccounts';
import { handleError } from './helperFunctions';

export const useAccounts = (isDisabled = false) => {
  const { usersStore } = useRootStore();
  const userAccountKey = usersStore?.currDispUserAccountKey;

  const queryClient = useQueryClient();
  const queryKey = [apiConstants.QUERY_KEYS.BILLING_ACCOUNTS];
  const { appStore } = useRootStore();
  return {
    reset: () => queryClient.resetQueries({ queryKey }),
    updateAccountProperties: () =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
      useMutation({
        mutationFn: (data) => updateAccountProperties(data),
        onError: () =>
          toast.error('Error updating account properties', {
            position: toast.POSITION.BOTTOM_RIGHT,
          }),
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey });
          queryClient.invalidateQueries({ queryKey: [apiConstants.QUERY_KEYS.MANAGEMENT_ACCOUNTS] });
        },
      }),
    deleteAccount: () =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
      useMutation({
        mutationFn: deleteAccount,
        onError: () =>
          toast.error('Error deleting account', {
            position: toast.POSITION.BOTTOM_RIGHT,
          }),
        onSuccess: () => {
          toast.success('Account deleted successfully');
          queryClient.invalidateQueries({ queryKey });
          queryClient.invalidateQueries({ queryKey: [apiConstants.QUERY_KEYS.MANAGEMENT_ACCOUNTS] });
        },
      }),
    fetchBillingAccountsData: (userKey, enabled = true) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
      useQuery({
        queryKey: [...queryKey, userKey],
        queryFn: fetchBillingAccounts,
        onError: () =>
          toast.error('Error fetching billing accounts data', {
            position: toast.POSITION.BOTTOM_RIGHT,
          }),
        enabled,
      }),
    fetchLinkedAccounts: (payerAccountId) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
      useQuery({
        queryKey: [...queryKey, payerAccountId],
        queryFn: () => fetchLinkedAccounts(payerAccountId),
        onError: () =>
          toast.error('Error fetching linked accounts data', {
            position: toast.POSITION.BOTTOM_RIGHT,
          }),
      }),
    fetchManagementAccountsData: ({ disabled }) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
      useQuery({
        queryKey: [apiConstants.QUERY_KEYS.MANAGEMENT_ACCOUNTS],
        queryFn: fetchManagementAccounts,
        staleTime: 5 * 60 * 1000,
        enabled: !disabled,
        onError: () =>
          toast.error('Error fetching management accounts data', {
            position: toast.POSITION.BOTTOM_RIGHT,
          }),
      }),
    invalidateManagementAccounts: () => {
      if (!appStore.isKeyCloakManagement) {
        return;
      }
      queryClient.invalidateQueries({ queryKey: [apiConstants.QUERY_KEYS.MANAGEMENT_ACCOUNTS] });
    },
    fetchDataAccessPayerAccounts: (isCustomersView, selectedCustomerDetails) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
      useQuery({
        queryKey: [
          userAccountKey,
          apiConstants.QUERY_KEYS.USER_MANAGEMENT_DATA_ACCESS_ACCOUNTS,
          isCustomersView,
          selectedCustomerDetails,
        ],
        queryFn: () => fetchDataAccessPayerAccounts(),
        enabled: !isDisabled,
        retry: false,
        staleTime: STALE_TIME,
        onError: handleError,
      }),
    fetchPayerLinkedAccounts: (payerId) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
      useQuery({
        queryKey: [userAccountKey, apiConstants.QUERY_KEYS.MANAGEMENT_ACCOUNTS, payerId],
        queryFn: () => fetchPayerLinkedAccounts(payerId),
        enabled: !!(!isDisabled && payerId),
        retry: false,
        staleTime: STALE_TIME,
        onError: handleError,
      }),
    updateLinkedAccountData: useMutation({
      mutationFn: ({ payerAccountId, linkedAccountId, linkedAccountData }) =>
        updateLinkedAccountData({ payerAccountId, linkedAccountId, linkedAccountData }),
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [userAccountKey, apiConstants.QUERY_KEYS.MANAGEMENT_ACCOUNTS],
        });
      },
      onError: handleError,
    }),
    verifyLinkedAccounts: useMutation({
      mutationFn: (linkedAccounts) => validateLinkedAccounts(linkedAccounts),
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [userAccountKey, apiConstants.QUERY_KEYS.MANAGEMENT_ACCOUNTS],
        });
      },
      onError: handleError,
    }),
  };
};
